import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BarChart3, ChevronDown, Play, User } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/scroll";
import { ASSETS } from "@/data/content";

const lineWrap = "block overflow-hidden";
const lineInner = (delay) => ({
  initial: { y: "112%" },
  animate: { y: "0%" },
  transition: { duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const ghostY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section ref={ref} className="relative flex min-h-svh items-end overflow-hidden pt-16" data-testid="hero-section">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <img
          src={ASSETS.hero}
          alt="Adrián Cuadra conduce el balón durante un partido con Deportes Antofagasta"
          className="h-[115%] w-full object-cover object-[center_20%]"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#07090E] via-[#07090E]/55 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#07090E] via-transparent to-[#07090E]/40" />

      <motion.span
        style={{ y: ghostY }}
        aria-hidden="true"
        className="text-ghost absolute -right-4 top-24 hidden select-none font-display text-[20rem] font-black leading-none md:block lg:text-[26rem]"
      >
        20
      </motion.span>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-14 sm:px-6 sm:pb-20 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mb-5 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-sky-400"
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="font-display font-black uppercase leading-[0.85] tracking-tight">
          <span className={lineWrap}>
            <motion.span {...lineInner(0.3)} className="block text-[clamp(3.8rem,13vw,10.5rem)] text-white">
              {t.hero.line1}
            </motion.span>
          </span>
          <span className={lineWrap}>
            <motion.span
              {...lineInner(0.44)}
              className="block bg-gradient-to-r from-sky-300 via-sky-400 to-[#00A3E0] bg-clip-text text-[clamp(3.8rem,13vw,10.5rem)] text-transparent"
            >
              {t.hero.line2}
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          className="mt-5 text-sm font-semibold uppercase tracking-[0.18em] text-slate-300 sm:text-base"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.88 }}
          className="mt-4 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
          data-testid="hero-phrase"
        >
          “{t.hero.phrase}”
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          <button
            data-testid="hero-cta-perfil"
            onClick={() => scrollToId("perfil")}
            className="group inline-flex items-center gap-2 rounded-full bg-sky-400 px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-[#07090E] transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_30px_rgba(56,189,248,0.35)]"
          >
            <User size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5" />
            {t.hero.ctaProfile}
          </button>
          <button
            data-testid="hero-cta-estadisticas"
            onClick={() => scrollToId("estadisticas")}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-200 transition-colors duration-300 hover:border-sky-400/60 hover:text-sky-300"
          >
            <BarChart3 size={15} />
            {t.hero.ctaStats}
          </button>
          <button
            data-testid="hero-cta-videos"
            onClick={() => scrollToId("videos")}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-200 transition-colors duration-300 hover:border-sky-400/60 hover:text-sky-300"
          >
            <Play size={15} />
            {t.hero.ctaVideos}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-4 py-2"
          data-testid="hero-contract-chip"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300">
            {t.hero.contract}
          </span>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-5 left-1/2 z-10 hidden -translate-x-1/2 sm:block"
      >
        <ChevronDown size={20} className="animate-bounce text-slate-500" />
      </motion.div>
    </section>
  );
}
